import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import {toast} from "react-toastify";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import SingleSelect from "../../components/SingleSelect";
import Loading from "../../components/loading";
import {
    loadActiveCompanies,
    markConversions,
    parseLeadLines,
    searchBrokers,
    countNonEmptyLines,
    LEADS_DOM_LINE_LIMIT,
} from "./uploadConversionsService";

const HP_TYPES = [
    {id: 1, label: "Fraud"},
    {id: 3, label: "Contact"},
    {id: 4, label: "Transfer"},
    {id: 5, label: "App"},
    {id: 6, label: "Enroll"},
    {id: 7, label: "Start"},
    {id: 8, label: "Remove"},
];

const LO_TYPES = [
    {id: 1, label: "Lead"},
    {id: 5, label: "App"},
    {id: 6, label: "Enroll"},
    {id: 7, label: "Start"},
    {id: -1, label: "Bad"},
    {id: 8, label: "Remove"},
];

const REMOVE_TYPE = 8;

export default function UploadConversionsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isLinkout = searchParams.get("tab") === "lo" || location.pathname === "/uploadConversionsLO";

    React.useEffect(() => {
        if (location.pathname === "/uploadConversionsLO" && searchParams.get("tab") !== "lo") {
            setSearchParams({tab: "lo"}, {replace: true});
        }
    }, [location.pathname, searchParams, setSearchParams]);

    const [broker, setBroker] = React.useState<any | null>(null);
    const [companyId, setCompanyId] = React.useState<number | "">("");
    const [conversionTypeId, setConversionTypeId] = React.useState<number | "">("");
    const [companies, setCompanies] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [loadingCompanies, setLoadingCompanies] = React.useState(true);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [confirmHelpOpen, setConfirmHelpOpen] = React.useState(false);
    const [lineCount, setLineCount] = React.useState(0);
    const [leadsBuffered, setLeadsBuffered] = React.useState(false);
    const [progress, setProgress] = React.useState<string | null>(null);
    const leadsRef = React.useRef<HTMLTextAreaElement | null>(null);
    const leadsDataRef = React.useRef("");
    const [lastResult, setLastResult] = React.useState<{
        matched: number;
        insertedOrUpdated: number;
        deleted: number;
        invalid: number;
    } | null>(null);

    const applyLeadsText = (text: string) => {
        leadsDataRef.current = text;
        const count = countNonEmptyLines(text);
        setLineCount(count);
        const buffered = count > LEADS_DOM_LINE_LIMIT;
        setLeadsBuffered(buffered);
        if (leadsRef.current) {
            // Keep huge pastes out of the DOM — that is what freezes weak machines.
            leadsRef.current.value = buffered ? "" : text;
        }
    };

    const clearLeads = () => {
        leadsDataRef.current = "";
        setLineCount(0);
        setLeadsBuffered(false);
        if (leadsRef.current) {
            leadsRef.current.value = "";
        }
    };

    const handleLeadsPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        applyLeadsText(e.clipboardData.getData("text/plain") || "");
    };

    const handleLeadsInput = () => {
        if (leadsBuffered) {
            return;
        }
        applyLeadsText(leadsRef.current?.value || "");
    };

    React.useEffect(() => {
        document.title = "Upload Conversions | DN";
        loadActiveCompanies().then((list) => {
            setCompanies(list);
            const dn = list.find((c: any) => c.name === "DN");
            setCompanyId(dn?.id ?? list[0]?.id ?? 1);
            setLoadingCompanies(false);
        });
    }, []);

    React.useEffect(() => {
        setConversionTypeId("");
        setLastResult(null);
    }, [isLinkout]);

    const typeOptions = isLinkout ? LO_TYPES : HP_TYPES;

    const handleTabChange = (_: React.SyntheticEvent, value: number) => {
        if (value === 1) {
            navigate("/uploadConversions?tab=lo");
        } else {
            navigate("/uploadConversions");
        }
    };

    const runSubmit = async () => {
        const leadsText = leadsDataRef.current || "";
        if (!leadsText.trim()) {
            toast.error("You must insert leads.");
            return;
        }
        if (!broker?.id) {
            toast.error("You must select a broker.");
            return;
        }
        if (companyId === "") {
            toast.error("You must select a company.");
            return;
        }
        if (conversionTypeId === "") {
            toast.error("You must select a conversion type.");
            return;
        }

        setLoading(true);
        setLastResult(null);
        setProgress("Parsing…");

        // Yield so the loading UI paints before parsing large pastes.
        await new Promise((r) => setTimeout(r, 0));

        const parsed = parseLeadLines(leadsText, !isLinkout);
        if (
            parsed.emails.length === 0
            && parsed.phones.length === 0
            && parsed.leadIds.length === 0
            && parsed.confirms.length === 0
        ) {
            setLoading(false);
            setProgress(null);
            toast.error(
                isLinkout
                    ? "No valid phone, email, or lead_id found."
                    : "No valid phone, email, lead_id, or confirm found.",
            );
            return;
        }

        setProgress("Marking…");
        const result = await markConversions(
            isLinkout,
            {
                brokerId: broker.id,
                companyId: Number(companyId),
                conversionTypeId: Number(conversionTypeId),
                emails: parsed.emails,
                phones: parsed.phones,
                leadIds: parsed.leadIds,
                confirms: isLinkout ? [] : parsed.confirms,
            },
            (done, total) => setProgress(`Marking… ${done}/${total}`),
        );

        setLoading(false);
        setProgress(null);

        if (!result) {
            return;
        }

        setLastResult({
            matched: result.matched,
            insertedOrUpdated: result.insertedOrUpdated,
            deleted: result.deleted,
            invalid: parsed.invalidCount,
        });

        if (Number(conversionTypeId) === REMOVE_TYPE) {
            toast.success(`Removed ${result.deleted} conversion(s)`);
        } else {
            toast.success(`Marked ${result.insertedOrUpdated} conversion(s)`);
        }
    };

    const handleSubmit = () => {
        if (conversionTypeId === REMOVE_TYPE) {
            setConfirmOpen(true);
            return;
        }
        void runSubmit();
    };

    return (
        <>
            <h2>Upload Conversions</h2>

            <Tabs
                value={isLinkout ? 1 : 0}
                onChange={handleTabChange}
                sx={{mb: 2}}
            >
                <Tab label="HP" />
                <Tab label="Linkout" />
            </Tabs>

            <p>
                {isLinkout
                    ? "Enter phone, email, or lead_id, one per line."
                    : (
                        <>
                            Enter phone, email, lead_id, or{" "}
                            <Link
                                component="button"
                                type="button"
                                onClick={() => setConfirmHelpOpen(true)}
                                sx={{
                                    verticalAlign: "baseline",
                                    fontWeight: 700,
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                }}
                            >
                                confirm fragment
                            </Link>
                            , one per line.
                        </>
                    )}
            </p>

            {loadingCompanies ? (
                <Loading />
            ) : (
                <Grid container spacing={2} sx={{maxWidth: 900}}>
                    <Grid item xs={12} md={7}>
                        {leadsBuffered && (
                            <Alert
                                severity="success"
                                sx={{mb: 1}}
                                action={
                                    <Button color="inherit" size="small" onClick={clearLeads} disabled={loading}>
                                        Clear
                                    </Button>
                                }
                            >
                                {lineCount.toLocaleString()} lines loaded in memory (not shown in the box — keeps the page responsive).
                                Paste again or Clear to replace.
                            </Alert>
                        )}
                        <TextField
                            label="Leads"
                            multiline
                            rows={18}
                            fullWidth
                            inputRef={leadsRef}
                            defaultValue=""
                            onPaste={handleLeadsPaste}
                            onInput={handleLeadsInput}
                            disabled={loading}
                            placeholder={leadsBuffered ? "Large paste held in memory — paste again to replace, or Clear" : undefined}
                            helperText={
                                leadsBuffered
                                    ? " "
                                    : (lineCount > 0 ? `${lineCount.toLocaleString()} non-empty line(s)` : " ")
                            }
                            FormHelperTextProps={{sx: {minHeight: 20, m: 0, mt: 0.5}}}
                            InputProps={{
                                readOnly: leadsBuffered,
                                sx: {
                                    height: 396,
                                    alignItems: "stretch",
                                    overflow: "hidden",
                                },
                            }}
                            inputProps={{
                                style: {
                                    height: "100%",
                                    overflow: "auto",
                                    resize: "none",
                                    boxSizing: "border-box",
                                },
                            }}
                            sx={{
                                "& .MuiInputBase-inputMultiline": {
                                    height: "100% !important",
                                    overflow: "auto !important",
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                            <Box>
                                <InputLabel sx={{mb: 0.5}}>Broker</InputLabel>
                                <SingleSelect
                                    searchFunction={searchBrokers}
                                    selectedItem={broker}
                                    setSelectedItem={setBroker}
                                    placeholder="Select Broker"
                                />
                            </Box>

                            <FormControl fullWidth>
                                <InputLabel id="conversion-type-label">Conversion Type</InputLabel>
                                <Select
                                    labelId="conversion-type-label"
                                    label="Conversion Type"
                                    value={conversionTypeId}
                                    onChange={(e) => setConversionTypeId(e.target.value as number)}
                                    disabled={loading}
                                >
                                    {typeOptions.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel id="company-label">Company</InputLabel>
                                <Select
                                    labelId="company-label"
                                    label="Company"
                                    value={companyId}
                                    onChange={(e) => setCompanyId(e.target.value as number)}
                                    disabled={loading}
                                >
                                    {companies.map((c: any) => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                Mark Conversions
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            )}

            {loading && (
                <Box sx={{mt: 2}}>
                    <Loading />
                    {progress && <Typography sx={{mt: 1}} color="text.secondary">{progress}</Typography>}
                </Box>
            )}

            {lastResult && (
                <Alert severity="info" sx={{mt: 2, maxWidth: 900}}>
                    Matched: {lastResult.matched}
                    {" · "}Inserted/Updated: {lastResult.insertedOrUpdated}
                    {" · "}Deleted: {lastResult.deleted}
                    {lastResult.invalid > 0 ? ` · Invalid/duplicate lines: ${lastResult.invalid}` : ""}
                </Alert>
            )}

            <Dialog open={confirmHelpOpen} onClose={() => setConfirmHelpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm fragments</DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Use a confirm fragment when you have a piece of the lead&apos;s stored confirm payload
                        (usually XML) and want to match leads whose <code>confirm</code> field contains that text.
                    </Typography>
                    <Typography paragraph>
                        A line is treated as a confirm fragment only if it contains at least one of{" "}
                        <code>-</code>, <code>&gt;</code>, or <code>&quot;</code>.
                        Matching is a substring search (<code>LIKE %value%</code>) against the lead confirm field.
                    </Typography>
                    <Typography paragraph sx={{mb: 1}}>
                        Example: if two leads have confirm fields like:
                    </Typography>
                    <Box
                        component="pre"
                        sx={{
                            bgcolor: "grey.100",
                            p: 1.5,
                            borderRadius: 1,
                            overflowX: "auto",
                            fontSize: 13,
                            mb: 2,
                        }}
                    >
{`Lead 1: <Success><confirmId>abcd1234</confirmId></Success>
Lead 2: <Success><confirmId>efgh5678</confirmId></Success>`}
                    </Box>
                    <Typography>
                        Upload these lines (one per line):
                    </Typography>
                    <Box
                        component="pre"
                        sx={{
                            bgcolor: "grey.100",
                            p: 1.5,
                            borderRadius: 1,
                            fontSize: 13,
                            mt: 1,
                        }}
                    >
{`>abcd1234
>efgh5678`}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmHelpOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Remove conversions?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove conversion types on these leads?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            setConfirmOpen(false);
                            void runSubmit();
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
